import React, { useState, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  MenuItem,
  CircularProgress,
  Link,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { submitSourceMaterials } from '../services/api/ApiService';

const US_STATES = [
  { code: '', name: '— Optional —' },
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
];

const MAX_FILES = 10;
const MAX_FILE_MB = 25;

const SubmitSourceMaterials = () => {
  const [form, setForm] = useState({
    description: '',
    source_urls: '',
    state_code: '',
    agency_hint: '',
    submitter_name: '',
    submitter_email: '',
    attestation: false,
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [referenceId, setReferenceId] = useState(null);
  const [submissionMessage, setSubmissionMessage] = useState(null);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleFileChange = (e) => {
    const picked = Array.from(e.target.files || []);
    if (files.length + picked.length > MAX_FILES) {
      setError(`You can attach at most ${MAX_FILES} files.`);
      return;
    }
    setFiles((prev) => [...prev, ...picked]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmissionMessage(null);

    if (!form.attestation) {
      setError('Please confirm you understand that submissions are quarantined for human review before processing.');
      return;
    }
    if (!form.description.trim() || form.description.trim().length < 20) {
      setError('Please provide a description of at least 20 characters.');
      return;
    }
    if (!form.source_urls.trim() && files.length === 0) {
      setError('Add at least one URL or file.');
      return;
    }

    setLoading(true);
    try {
      const body = new FormData(e.currentTarget);
      files.forEach((file) => {
        body.append('files', file);
      });
      const result = await submitSourceMaterials(body);
      if (result?.reference_id) {
        setReferenceId(result.reference_id);
      } else {
        setSubmissionMessage(
          result?.message ||
            'Submission received. Our team will review your materials before any processing.'
        );
      }
      setForm({
        description: '',
        source_urls: '',
        state_code: '',
        agency_hint: '',
        submitter_name: '',
        submitter_email: '',
        attestation: false,
      });
      setFiles([]);
    } catch (err) {
      setError(err.message || 'Submission failed. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  if (referenceId) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Submission received
          </Typography>
          <Typography variant="body1" paragraph>
            Your materials have been quarantined for review by our team. We do not process uploads
            automatically into the public database.
          </Typography>
          <Typography variant="body2">
            <strong>Reference ID:</strong> {referenceId}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Save this ID if you need to follow up. We may contact you at the email provided, if any.
          </Typography>
        </Alert>
        <Button variant="outlined" onClick={() => setReferenceId(null)}>
          Submit another
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
          Submit Misconduct
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 720, mx: 'auto' }}>
          Share records, documents, or links that may document judicial or prosecutorial
          misconduct. Uploads are stored securely and reviewed before any use in our database.
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        You may upload any relevant materials. Submissions are stored securely and reviewed before
        any use. Allowed file types include PDF, images, text, CSV, and HTML. Maximum{' '}
        {MAX_FILE_MB}MB per file, {MAX_FILES} files per submission.
      </Alert>

      {submissionMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSubmissionMessage(null)}>
          {submissionMessage}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper component="form" onSubmit={handleSubmit} sx={{ p: 4 }}>
        <TextField
          fullWidth
          required
          multiline
          minRows={4}
          label="What are you submitting?"
          name="description"
          value={form.description}
          onChange={handleChange}
          helperText="Describe the source, jurisdiction, and why it may be relevant (min. 20 characters)."
          sx={{ mb: 3 }}
        />

        <TextField
          fullWidth
          multiline
          minRows={2}
          label="URLs (one per line)"
          name="source_urls"
          value={form.source_urls}
          onChange={handleChange}
          placeholder="https://example.gov/disciplinary-records/..."
          helperText="http or https links only"
          sx={{ mb: 3 }}
        />

        <TextField
          select
          fullWidth
          label="State (optional)"
          name="state_code"
          value={form.state_code}
          onChange={handleChange}
          sx={{ mb: 3 }}
        >
          {US_STATES.map((s) => (
            <MenuItem key={s.code || 'none'} value={s.code}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          label="Agency or court (optional)"
          name="agency_hint"
          value={form.agency_hint}
          onChange={handleChange}
          sx={{ mb: 3 }}
        />

        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <TextField
            label="Your name (optional)"
            name="submitter_name"
            value={form.submitter_name}
            onChange={handleChange}
            sx={{ flex: 1, minWidth: 200 }}
          />
          <TextField
            label="Email (optional)"
            name="submitter_email"
            type="email"
            value={form.submitter_email}
            onChange={handleChange}
            sx={{ flex: 1, minWidth: 200 }}
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.txt,.csv,.json,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.htm,.html,.rtf"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={files.length >= MAX_FILES}
          >
            Attach files ({files.length}/{MAX_FILES})
          </Button>
          {files.length > 0 && (
            <List dense sx={{ mt: 2 }}>
              {files.map((file, index) => (
                <ListItem key={`${file.name}-${index}`}>
                  <ListItemText
                    primary={file.name}
                    secondary={`${(file.size / 1024).toFixed(1)} KB`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => removeFile(index)} aria-label="remove">
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        {/* Honeypot — hidden from users */}
        <Box
          component="label"
          aria-hidden="true"
          sx={{
            position: 'absolute',
            left: -9999,
            width: 1,
            height: 1,
            overflow: 'hidden',
          }}
        >
          Website
          <input type="text" name="website" tabIndex={-1} autoComplete="off" />
        </Box>

        <Alert severity="warning" sx={{ mb: 2 }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
            <input
              type="checkbox"
              name="attestation"
              checked={form.attestation}
              onChange={handleChange}
              style={{ marginTop: 4 }}
            />
            <span>
              I understand uploads are quarantined for human review before any processing or
              publication.
            </span>
          </label>
        </Alert>

        <Button
          type="submit"
          variant="contained"
          size="large"
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
          disabled={loading}
          fullWidth
        >
          {loading ? 'Uploading…' : 'Submit for review'}
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, textAlign: 'center' }}>
          {loading
            ? 'Uploading files and saving submission...'
            : 'After submission, you will see a confirmation message and reference ID.'}
        </Typography>
      </Paper>

      <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
        Looking to report personal misconduct? See our{' '}
        <Link href="/submit-case">questionnaire request</Link> instead.
      </Typography>
    </Container>
  );
};

export default SubmitSourceMaterials;
